// src/types/models.ts
import { User } from "./users";
import { Season } from "./common";
import { ID } from "./common";

export interface EventLocation {
  id: ID;
  name: string;
  address: string;
  city: string;
  country: string;
  zip_code: string;
  latitude: number;
  longitude: number;
}

export interface Tournament {
  id: ID;
  name: string;
  tournament_points: number;
}

export type TournamentEventState = 'da_programmare' | 'da_confermare' | 'confermato' | 'in_corso' | 'concluso';
export const TranslationEventState: Record<TournamentEventState, string> = {
  da_programmare: "Da programmare",
  da_confermare: "Da confermare",
  confermato: "Confermato",
  in_corso: "In corso",
  concluso: "Concluso",
}
export const StateToInt = {
  "da_programmare": 1,
  "da_confermare": 2,
  "confermato": 3,
  "in_corso": 4,
  "concluso": 5,
}
export interface TournamentEvent {
  id: ID;
  tournament: Tournament;
  season: Season;
  state: TournamentEventState; // stato dell'evento
  date: string;       // formato "YYYY-MM-DD"
  time: string;       // formato "HH:MM:SS"
  location: EventLocation;
  results: TournamentResult[];
  tournament_points: number;
  can_generate_results_automatically: boolean;
}
export type ApiTournamentEvent = (
  Omit<TournamentEvent, "results" | "can_generate_results_automatically">
  & { 
    tournament_id: ID; 
    season_id: ID; 
    location_id: ID;
    results: Partial<ApiTournamentResult>[];
  }
)

export interface TournamentResult {
  id: ID;
  event: TournamentEvent;
  user: User;
  position: number;
  score: number;
}
export type ApiTournamentResult = Omit<TournamentResult, ('id' | 'event' | 'user')> & { event_id: ID; user_id: ID; }


export interface TournamentParticipant {
  id: ID
  event: ID
  name: string
  players: ID[]
  seed: number | null
  created_at: string
}
export interface TournamentParticipantExpanded extends TournamentParticipant {
  players_objects?: User[]
}

export type StageType =
  | "groups"
  | "knockout"
export const STAGE_TYPE: Record<StageType, string> = {
  "groups": "Gironi",
  "knockout": "Eliminatorie"
}

export type SeedingMethod =
  | "random"
  | "weighted"
export const SEEDING_METHOD: Record<SeedingMethod, string> = {
  "random": "Random",
  "weighted": "A fasce"
}

export type TieBreakerMethod =
  | "direct_comparison"
  | "point_difference"
  | "points_done"
  | "points_against"

export const TIE_BREAKER_METHOD: Record<TieBreakerMethod, string> = {
  "direct_comparison": "Confronto diretto",
  "point_difference": "Differenza di punti",
  "points_done": "Punti fatti",
  "points_against": "Punti subiti"
}

export const TIE_BREAKER_OPTIONS: { value: TieBreakerMethod; label: string }[] = [
  { value: "direct_comparison", label: "Scontri diretti" },
  { value: "point_difference", label: "Differenza punti" },
  { value: "points_done", label: "Punti fatti" },
  { value: "points_against", label: "Punti subiti" },
]

export type MatchFormat =
  | "single"
  | "double"
export const MATCH_FORMAT: Record<MatchFormat, string> = {
  "single": "Sola andata",
  "double": "Andata e ritorno"
}

export type KnockoutLevel =
  | "round_of_16"
  | "quarter_finals"
  | "semi_finals"
  | "final"
export const KNOCKOUT_LEVEL: Record<KnockoutLevel, string> = {
  "round_of_16": "Ottavi",
  "quarter_finals": "Quarti",
  "semi_finals": "Semifinali",
  "final": "Finale"
}

export type CompetitionStageStatus =
  | "scheduled"
  | "ongoing"
  | "played"
export const COMPETITION_STAGE_STATUS: Record<CompetitionStageStatus, string> = {
  "scheduled": "Programmato",
  "ongoing": "In corso",
  "played": "Giocato"
}

export interface CompetitionStage {
  id: ID
  event: ID
  source_stage: ID | null

  name: string
  stage_type: StageType
  order: number

  match_format: MatchFormat | null

  // groups
  num_groups: number | null
  seeding_method: SeedingMethod | null
  qualifiers_per_group: number | null
  wildcards: number | null
  tie_breakers: string[]

  // knockout
  knockout_stage_type: KnockoutLevel | null

  ranking_start_position: number | null

  participants: ID[]
  
  readonly status: CompetitionStageStatus
}

export type CompetitionStageWritePayload = Partial<Omit<CompetitionStage, "id" | "status" | "source_stage">>

export interface CompetitionGroup {
  id: ID
  stage: ID
  name: string
  participants: ID[]
}
export interface CompetitionGroupExpanded extends CompetitionGroup {
  participants_objects?: TournamentParticipant[]
}

export type MatchStatus =
  | "scheduled"
  | "ongoing"
  | "played"
export const MATCH_STATUS: Record<MatchStatus, string> = {
  "scheduled": "Programmato",
  "ongoing": "In corso",
  "played": "Giocato"
}

export interface Match {
  id: ID

  stage: ID
  group: ID | null

  player1: ID | null
  player2: ID | null

  score1: number | null
  score2: number | null

  winner: ID | null

  order: number | null

  status: MatchStatus

  scheduled_at: string | null
  duration_minutes: number

  next_winner_match: ID | null
  next_winner_slot: number | null

  next_loser_match: ID | null
  next_loser_slot: number | null

  ranking_start_position: number | null
  ranking_end_position: number | null
}

export interface GroupStanding {
  id: ID
  group: ID
  participant: ID

  played: number
  wins: number
  draws: number
  losses: number

  points_scored: number
  points_conceded: number

  points: number

  position: number | null
}

export interface MatchExpanded extends Match {
  player1_object?: TournamentParticipant
  player2_object?: TournamentParticipant
  winner_object?: TournamentParticipant
  group_object?: CompetitionGroup
}

export interface GroupWithStandings extends CompetitionGroup {
  standings: GroupStanding[]
}

export interface TournamentCompetitionState {
  stages: CompetitionStage[]
  groups: CompetitionGroup[]
  matches: Match[]
  standings: GroupStanding[]
  participants: TournamentParticipant[]
}
